import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { WishlistState } from "@/types/cart";
import { Product } from "@/types/shop";
import {
  getWishlist,
  addToWishlistApi,
  removeFromWishlistApi,
  clearWishlistApi,
} from "@/services/wishlistService";

const GUEST_WISHLIST_KEY = "hubmicroo_guest_wishlist";

const getStoredWishlist = (): Product[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(GUEST_WISHLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => item && (item._id || item.id)) : [];
  } catch {
    return [];
  }
};

const saveStoredWishlist = (items: Product[]) => {
  if (typeof window === "undefined") return;
  try {
    const validItems = Array.isArray(items) ? items.filter((item) => item && item._id) : [];
    localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(validItems));
  } catch {}
};

interface ExtendedWishlistState extends WishlistState {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ExtendedWishlistState = {
  items: getStoredWishlist(),
  status: "idle",
  error: null,
};

// Async Thunks with Dual-Mode (Guest + Auth) & LocalStorage Sync

export const fetchWishlist = createAsyncThunk(
  "wishlist/fetchWishlist",
  async (_, { rejectWithValue }) => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (!token) {
      return getStoredWishlist();
    }

    try {
      const response = await getWishlist();

      let products: Product[] = [];
      const dataObj = response?.data || response;

      if (dataObj && Array.isArray(dataObj.products)) {
        products = dataObj.products
          .filter((item: any) => item && (item.product || item._id))
          .map((item: any) => {
            if (item.product && typeof item.product === "object") {
              return {
                ...item.product,
                _id: item.product._id || item.product.id,
              };
            }
            return {
              ...item,
              _id: item._id || item.id,
            };
          });
      } else if (Array.isArray(dataObj)) {
        products = dataObj.filter((item: any) => item && (item._id || item.id));
      } else if (Array.isArray(response?.products)) {
        products = response.products
          .filter((item: any) => item && (item.product || item._id))
          .map((item: any) => {
            if (item.product && typeof item.product === "object") {
              return {
                ...item.product,
                _id: item.product._id || item.product.id,
              };
            }
            return item;
          });
      }

      // Merge local items if server wishlist was empty, or use server items
      if (products.length > 0) {
        saveStoredWishlist(products);
        return products;
      } else {
        const local = getStoredWishlist();
        return local;
      }
    } catch (error: any) {
      return getStoredWishlist();
    }
  }
);

export const addToWishlist = createAsyncThunk(
  "wishlist/addToWishlist",
  async (product: Product, { rejectWithValue }) => {
    const prodId = product._id || (product as any).id;
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (token && prodId) {
      try {
        await addToWishlistApi(prodId);
      } catch (error: any) {
        console.warn("API add to wishlist notice:", error?.message);
      }
    }

    return {
      ...product,
      _id: prodId,
    };
  }
);

export const removeFromWishlist = createAsyncThunk(
  "wishlist/removeFromWishlist",
  async (productId: string, { rejectWithValue }) => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (token && productId) {
      try {
        await removeFromWishlistApi(productId);
      } catch (error: any) {
        console.warn("API remove from wishlist notice:", error?.message);
      }
    }

    return productId;
  }
);

export const clearWishlist = createAsyncThunk(
  "wishlist/clearWishlist",
  async (_, { rejectWithValue }) => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (token) {
      try {
        await clearWishlistApi();
      } catch (error: any) {
        console.warn("API clear wishlist error:", error?.message);
      }
    }

    saveStoredWishlist([]);
    return;
  }
);

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    syncWishlistFromStorage: (state) => {
      state.items = getStoredWishlist();
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload || [];
        saveStoredWishlist(state.items);
      })
      .addCase(fetchWishlist.rejected, (state) => {
        state.status = "failed";
        state.items = getStoredWishlist();
      })
      // Add to Wishlist
      .addCase(addToWishlist.fulfilled, (state, action) => {
        const prodId = action.payload._id || (action.payload as any).id;
        const exists = state.items.some(
          (item) => (item._id || (item as any).id) === prodId
        );
        if (!exists) {
          state.items.push(action.payload);
          saveStoredWishlist(state.items);
        }
      })
      // Remove from Wishlist
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        const targetId = action.payload;
        state.items = state.items.filter(
          (item) => (item._id || (item as any).id) !== targetId
        );
        saveStoredWishlist(state.items);
      })
      // Clear Wishlist
      .addCase(clearWishlist.fulfilled, (state) => {
        state.items = [];
        saveStoredWishlist([]);
      });
  },
});

export const { syncWishlistFromStorage } = wishlistSlice.actions;
export default wishlistSlice.reducer;
